import React, {useState} from 'react'
import {motion, AnimatePresence} from 'framer-motion'
import {SupabaseService} from '../../services/supabaseService'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {FiChevronDown, FiCheck, FiLoader} = FiIcons

const StatusDropdownDirectory = ({opportunityId, initialStatus = 'New', user, onStatusUpdate, onStatusChange}) => {
  const [status, setStatus] = useState(initialStatus)
  const [isOpen, setIsOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const statusOptions = [
    {value: 'New', label: 'New', color: 'green-btn text-white'},
    {value: 'Submitted', label: 'Submitted', color: 'bg-orange-500/70 text-white'},
    {value: 'Live', label: 'Live', color: 'bg-yellow-500/70 text-white'}
  ]

  const getStatusColor = (statusValue) => {
    const option = statusOptions.find(opt => opt.value === statusValue)
    return option?.color || 'bg-gray-500 text-white'
  }

  const getStatusLabel = (statusValue) => {
    const option = statusOptions.find(opt => opt.value === statusValue)
    return option?.label || 'Unknown'
  }

  const toggleDropdown = () => {
    if (!isSaving) {
      setIsOpen(!isOpen)
    }
  }

  const handleStatusChange = async (newStatus) => {
    if (newStatus === status) {
      setIsOpen(false)
      return
    }

    setIsSaving(true)
    try {
      // Update local state immediately
      setStatus(newStatus)

      // Save to backend
      await SupabaseService.updateDirectoryOpportunityStatus(
        opportunityId,
        newStatus
      )

      // Update user action in user_link_directory_actions table
      if (onStatusChange) {
        onStatusChange(opportunityId, newStatus)
      }

      if (onStatusUpdate) {
        onStatusUpdate()
      }
    } catch (error) {
      console.error('Failed to update status:', error)
      // Revert local state on error
      setStatus(initialStatus)
    } finally {
      setIsSaving(false)
      setIsOpen(false)
    }
  }

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        disabled={isSaving}
        className={`flex items-center justify-between rounded-md px-3 py-1.5 text-xs font-medium ${getStatusColor(status)} min-w-[100px] ${
          isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
        }`}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span>{getStatusLabel(status)}</span>
        {isSaving ? (
          <SafeIcon icon={FiLoader} className="w-3 h-3 ml-1 animate-spin" />
        ) : (
          <SafeIcon icon={FiChevronDown} className="w-3 h-3 ml-1" />
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{opacity: 0, y: -10}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: -10}}
            transition={{duration: 0.2}}
            className="absolute right-0 mt-1 w-36 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
          >
            <div className="py-1" role="menu">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleStatusChange(option.value)}
                  className={`flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100 ${
                    status === option.value ? 'font-medium' : 'font-normal'
                  }`}
                  role="menuitem"
                >
                  <span className={`w-3 h-3 rounded-full mr-2 ${option.color}`}></span>
                  {option.label}
                  {status === option.value && (
                    <SafeIcon icon={FiCheck} className="w-3 h-3 ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default StatusDropdownDirectory
